import type { FC } from 'react';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { JobDetailsVisibleJobFragment } from '@xing-com/crate-jobs-graphql';
import { BodyCopy } from '@xing-com/typography';

import { Container, OverflowLabel } from './multi-location-display.styles';
import { useVisibleChildren } from './use-visible-children';

type MultiLocationDisplayProps = {
  lineHeight?: 'normal' | 'dense' | undefined;
  lines?: 1 | 2;
  locations: JobDetailsVisibleJobFragment['locations'];
  size?: 'small' | 'medium' | 'large';
};

export const MultiLocationDisplay: FC<MultiLocationDisplayProps> = ({
  lineHeight,
  lines = 1,
  locations,
  size = 'medium',
}) => {
  const intl = useIntl();
  const containerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLParagraphElement>(null);
  const overflowLabelRef = useRef<HTMLParagraphElement>(null);
  // Filter out null/undefined values from the locations array
  const filteredLocations = locations
    .map((location) => location.city)
    .filter(Boolean);
  const [truncatedCity, setTruncatedCity] = useState(
    filteredLocations[0] || ''
  );

  const { hasMeasured, hiddenCount, visibleCount } = useVisibleChildren(
    containerRef,
    wrapperRef,
    overflowLabelRef,
    filteredLocations.length,
    lines
  );

  // Helper to truncate the first city to fit
  // This also makes the case where the first city fits fully but the overflow label does not fit
  const getTruncatedCity = useCallback((): string => {
    const firstCity = filteredLocations[0] || '';
    if (!containerRef.current || !overflowLabelRef.current || hiddenCount === 0)
      return firstCity;
    const containerWidth = containerRef.current.clientWidth;
    const overflowLabelWidth = overflowLabelRef.current.offsetWidth;
    const availableWidth = containerWidth - overflowLabelWidth;
    const testSpan = document.createElement('p');
    testSpan.style.visibility = 'hidden';
    testSpan.style.position = 'absolute';
    testSpan.style.lineHeight = wrapperRef.current?.style.lineHeight || '1.25';
    testSpan.style.fontWeight = wrapperRef.current?.style.fontWeight || '400';
    testSpan.style.fontSize = wrapperRef.current?.style.fontSize || '16px';
    testSpan.style.margin = wrapperRef.current?.style.margin || '0';
    document.body.appendChild(testSpan);

    let truncated = firstCity;
    testSpan.textContent = firstCity;

    try {
      for (let i = firstCity.length || 0; i > 0; i--) {
        // check if full city fits
        if (i === firstCity.length && testSpan.offsetWidth < availableWidth) {
          break;
        }
        testSpan.textContent = firstCity.slice(0, i) + '...';
        if (testSpan.offsetWidth < availableWidth) {
          truncated = firstCity.slice(0, i) + '...';
          break;
        }
      }
    } finally {
      document.body.removeChild(testSpan);
    }
    return truncated;
  }, [filteredLocations, hiddenCount]);

  useLayoutEffect(() => {
    if (visibleCount === 1 && hasMeasured) {
      setTruncatedCity(getTruncatedCity());
    }
  }, [getTruncatedCity, hasMeasured, visibleCount]);

  const visibleLocations = filteredLocations.slice(0, visibleCount);

  return (
    <Container lineClampProp={lines} ref={containerRef}>
      <BodyCopy
        {...(lineHeight !== undefined ? { lineHeight: 'dense' } : {})}
        noMargin
        ref={wrapperRef}
        size={size}
      >
        {visibleCount === 1
          ? truncatedCity
          : visibleLocations.map((loc, i) => (
              <span key={`${i}-${loc}`}>
                {loc}
                {i < visibleLocations.length - 1 ? ', ' : ''}
              </span>
            ))}
        <OverflowLabel
          ref={overflowLabelRef}
          className={
            !hasMeasured || (hiddenCount > 0 && locations.length > 1)
              ? 'visible'
              : ''
          }
          {...(hiddenCount > 0 && { 'aria-hidden': 'true' })}
        >
          &nbsp;
          {intl.formatMessage(
            { id: 'JOBS_MULTILOCATION_MORE_LABEL' },
            { variable_1: visibleCount === 0 ? hiddenCount - 1 : hiddenCount }
          )}
        </OverflowLabel>
      </BodyCopy>
    </Container>
  );
};
